import React from "react";
import { observer } from "mobx-react";
import { TenantCreateStore } from "../../store";
import { TenantCreateForm } from "./TenantCreateForm";
import { ErrorDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { EsignatureProvider } from "../../../../api/graphql";

export type TenantCreateProps = {
  tenantCreateStore: TenantCreateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const TenantCreate: React.FC<TenantCreateProps> = observer(
  ({ tenantCreateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={tenantCreateStore.isSaving} />
        <TenantCreateForm
          tenantCreateStore={tenantCreateStore}
          nameError={tenantCreateStore.errors.name?.message}
          onNameChange={(value: string) => {
            tenantCreateStore.setNameValue(value);
          }}
          name={tenantCreateStore.entityValues.name}
          tenantTypeError={tenantCreateStore.errors.tenantType?.message}
          onTenantTypeChange={(value) => {
            tenantCreateStore.setTenantTypeValue(value);
          }}
          tenantTypeSelectedValue={
            tenantCreateStore.entityValues.showTenantType
          }
          onTenantTypeSelected={(value) => {
            tenantCreateStore.setSelectedTenantType(value);
          }}
          tenantTypeOptions={tenantCreateStore.getTenantTypeOptions()}
          statusError={tenantCreateStore.errors.status?.message}
          onStatusChange={(value) => {
            tenantCreateStore.setStatusValue(value);
          }}
          statusSelectedValue={tenantCreateStore.entityValues.showStatus}
          onStatusSelected={(value) => {
            tenantCreateStore.setSelectedStatus(value);
          }}
          statusOptions={tenantCreateStore.getStatusOptions()}
          planError={tenantCreateStore.errors.plan?.message}
          onPlanChange={(value) => {
            tenantCreateStore.setPlanValue(value);
          }}
          planSelectedValue={tenantCreateStore.entityValues.showPlan}
          onPlanSelected={(value) => {
            tenantCreateStore.setSelectedPlan(value);
          }}
          planOptions={tenantCreateStore.getPlanOptions()}
          logoOnRestoreFile={(id: string) =>
            tenantCreateStore.logoRestoreFile(id)
          }
          logoOnLoadFile={(file: any) => tenantCreateStore.logoUploadFile(file)}
          logoOnCommitFile={(changes) =>
            tenantCreateStore.logoCommitFile(changes)
          }
          logoFiles={tenantCreateStore.entityValues.__logo_files}
          logoError={tenantCreateStore.errors.logo?.message}
          publicUrlError={tenantCreateStore.errors.publicUrl?.message}
          onPublicUrlChange={(value: string) => {
            tenantCreateStore.setPublicUrlValue(value);
          }}
          publicUrl={tenantCreateStore.entityValues.publicUrl}
          idERPError={tenantCreateStore.errors.idERP?.message}
          onIdERPChange={(value: string) => {
            tenantCreateStore.setIdERPValue(value);
          }}
          idERP={tenantCreateStore.entityValues.idERP}
          idCRMError={tenantCreateStore.errors.idCRM?.message}
          onIdCRMChange={(value: string) => {
            tenantCreateStore.setIdCRMValue(value);
          }}
          idCRM={tenantCreateStore.entityValues.idCRM}
          monthlyTokenLimitError={
            tenantCreateStore.errors.monthlyTokenLimit?.message
          }
          onMonthlyTokenLimitChange={(value: number | undefined) => {
            tenantCreateStore.setMonthlyTokenLimitValue(value);
          }}
          monthlyTokenLimit={tenantCreateStore.entityValues.monthlyTokenLimit}
          currentMonthTokensError={
            tenantCreateStore.errors.currentMonthTokens?.message
          }
          onCurrentMonthTokensChange={(value: number) => {
            tenantCreateStore.setCurrentMonthTokensValue(value);
          }}
          currentMonthTokens={tenantCreateStore.entityValues.currentMonthTokens}
          tokenLimitResetDayError={
            tenantCreateStore.errors.tokenLimitResetDay?.message
          }
          onTokenLimitResetDayChange={(value: number) => {
            tenantCreateStore.setTokenLimitResetDayValue(value);
          }}
          tokenLimitResetDay={tenantCreateStore.entityValues.tokenLimitResetDay}
          lastTokenResetDateError={
            tenantCreateStore.errors.lastTokenResetDate?.message
          }
          onLastTokenResetDateChange={(value) => {
            tenantCreateStore.setLastTokenResetDateValue(value);
          }}
          lastTokenResetDate={tenantCreateStore.entityValues.lastTokenResetDate}
          ragLlmProviderError={tenantCreateStore.errors.ragLlmProvider?.message}
          onRagLlmProviderChange={(value) => {
            tenantCreateStore.setRagLlmProviderValue(value);
          }}
          ragLlmProviderSelectedValue={
            tenantCreateStore.entityValues.showRagLlmProvider
          }
          onRagLlmProviderSelected={(value) => {
            tenantCreateStore.setSelectedRagLlmProvider(value);
          }}
          ragLlmProviderOptions={tenantCreateStore.getRagLlmProviderOptions()}
          ragLlmModelError={tenantCreateStore.errors.ragLlmModel?.message}
          onRagLlmModelChange={(value: string) => {
            tenantCreateStore.setRagLlmModelValue(value);
          }}
          ragLlmModel={tenantCreateStore.entityValues.ragLlmModel}
          currentStorageBytesError={
            tenantCreateStore.errors.currentStorageBytes?.message
          }
          onCurrentStorageBytesChange={(value: number) => {
            tenantCreateStore.setCurrentStorageBytesValue(value);
          }}
          currentStorageBytes={
            tenantCreateStore.entityValues.currentStorageBytes
          }
          storageByteLimitError={
            tenantCreateStore.errors.storageByteLimit?.message
          }
          onStorageByteLimitChange={(value: number | undefined) => {
            tenantCreateStore.setStorageByteLimitValue(value);
          }}
          storageByteLimit={tenantCreateStore.entityValues.storageByteLimit}
          eSignatureProviderError={
            tenantCreateStore.errors.eSignatureProvider?.message
          }
          eSignatureProviderOptions={tenantCreateStore.getESignatureProviderOptions()}
          onESignatureProviderChange={(value: EsignatureProvider | "") => {
            tenantCreateStore.setESignatureProviderValue(value);
          }}
          eSignatureProvider={tenantCreateStore.entityValues.eSignatureProvider}
          dummyCRUDRestoredObservable={
            tenantCreateStore.dummyCRUDRestoredObservable
          }
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={tenantCreateStore.isProcessing}
          onSave={() => tenantCreateStore.saveNewTenant()}
          onCancel={() => tenantCreateStore.cancelNewTenant()}
        />
        <ErrorDialog
          open={tenantCreateStore.errorDialogOpened}
          title={tenantCreateStore.errorTitle}
          description={tenantCreateStore.errorDescription}
          onClose={() => tenantCreateStore.closeErrorDialog()}
        />
      </>
    );
  }
);
